import React, { useState, useEffect, useCallback } from 'react';
import { useBranch } from './../../layouts/BranchContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  styled,
  Modal,
  IconButton,
  useTheme,
  Snackbar,
  CircularProgress,
  Chip,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  Tooltip,
  ToggleButton,
  InputAdornment,
  ToggleButtonGroup,
  Checkbox,
  Popover,
} from '@mui/material';

import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ClearIcon from '@mui/icons-material/Clear';
import MuiAlert from '@mui/material/Alert';

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '6px',
    width: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.2)'
        : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${
    theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.2)'
      : 'rgba(0,0,0,0.2)'
  } transparent`,
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return (
    <MuiAlert
      elevation={6}
      ref={ref}
      variant="filled"
      {...props}
    />
  );
});

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  'https://wims.revisetoexcel.com';

const STATUS_OPTIONS = [
  {
    value: 'present',
    label: 'Present',
    shortLabel: 'P',
    color: 'success',
  },
  {
    value: 'absent',
    label: 'Absent',
    shortLabel: 'A',
    color: 'error',
  },
  {
    value: 'half_day',
    label: 'Half Day',
    shortLabel: 'H',
    color: 'warning',
  },
  {
    value: 'on_leave',
    label: 'On Leave',
    shortLabel: 'L',
    color: 'info',
  },
];

const getStatusMeta = (status) => {
  const match = STATUS_OPTIONS.find((s) => s.value === status);

  if (!match) {
    return {
      label: status || 'Unknown',
      shortLabel: '-',
      color: 'default',
      icon: undefined,
    };
  }

  return {
    label: match.label,
    shortLabel: match.shortLabel,
    color: match.color,
    icon:
      status === 'present' ? (
        <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
      ) : status === 'absent' ? (
        <HighlightOffIcon sx={{ fontSize: 14 }} />
      ) : undefined,
  };
};

const getAuthToken = () => (
  localStorage.getItem('accessToken') ||
  localStorage.getItem('token') ||
  localStorage.getItem('authToken') ||
  localStorage.getItem('access_token') ||
  sessionStorage.getItem('accessToken') ||
  sessionStorage.getItem('token')
);

const safeParseJSON = (str, fallback = {}) => {
  try {
    return JSON.parse(str) || fallback;
  } catch {
    return fallback;
  }
};

/* =========================================================
   TIME HELPERS
========================================================= */

const timeToMinutes = (value) => {
  if (!value || typeof value !== 'string') return null;

  const match = value
    .trim()
    .match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);

  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);

  if (
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  return hours * 60 + minutes;
};

const parseFixedPeriodTime = (value, period) => {
  if (!value || typeof value !== 'string') return '';

  const match = value
    .trim()
    .match(/^(\d{1,2}):([0-5]\d)$/);

  if (!match) return '';

  let hours = Number(match[1]);
  const minutes = Number(match[2]);

  if (hours < 1 || hours > 12) return '';

  if (period === 'AM') {
    if (hours === 12) hours = 0;
  } else if (period === 'PM') {
    if (hours !== 12) hours += 12;
  }

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
    2,
    '0'
  )}`;
};

const formatAmPmTime = (value) => {
  const minutes = timeToMinutes(value);

  if (minutes === null) return '';

  let hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  const period = hours >= 12 ? 'PM' : 'AM';

  hours = hours % 12 || 12;

  return `${String(hours).padStart(2, '0')}:${String(
    mins
  ).padStart(2, '0')} ${period}`;
};

const normalizeTime = (value) => {
  const minutes = timeToMinutes(value);

  if (minutes === null) return value || '';

  const hours = String(Math.floor(minutes / 60)).padStart(2, '0');
  const mins = String(minutes % 60).padStart(2, '0');

  return `${hours}:${mins}`;
};

const getPeriodFromTime = (value, fallback = 'AM') => {
  const minutes = timeToMinutes(value);

  if (minutes === null) return fallback;

  return minutes >= 720 ? 'PM' : 'AM';
};

const normalize12Input = (value) => {
  const cleaned = String(value || '')
    .replace(/[^0-9:]/g, '')
    .slice(0, 5);

  if (!cleaned) return '';

  if (cleaned.includes(':')) {
    const [h = '', m = ''] = cleaned.split(':');

    const hour = h.slice(0, 2);
    const minute = m.slice(0, 2);

    return minute ? `${hour}:${minute}` : hour;
  }

  if (cleaned.length <= 2) {
    return cleaned;
  }

  return `${cleaned.slice(0, 2)}:${cleaned.slice(2, 4)}`;
};

/* =========================================================
   COMPONENT
========================================================= */

const AddAttendance = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const currentUser = safeParseJSON(
    sessionStorage.getItem('user')
  );

  const role = currentUser?.role;

  const { selectedBranchId } = useBranch();

  /* =========================================================
     STYLES
  ========================================================= */

  const labelSx = {
    '& .MuiInputLabel-root': {
      color: theme.palette.text.primary,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.primary.main,
    },
    '& .MuiInputBase-input': {
      color: theme.palette.text.primary,
    },
    '& .MuiInputBase-input::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
    '& .MuiSelect-icon': {
      color: theme.palette.text.primary,
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
      color: theme.palette.text.secondary,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.primary,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '& input::-webkit-calendar-picker-indicator': {
      filter:
        theme.palette.mode === 'dark'
          ? 'invert(1)'
          : 'none',
    },
  };

  const noUnderlineSx = {
    '& .MuiInput-underline:before': {
      display: 'none',
    },
    '& .MuiInput-underline:after': {
      display: 'none',
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
      display: 'none',
    },
    '& .MuiInputBase-root': {
      borderBottom: 'none',
    },
    '& .MuiInputBase-root:before': {
      borderBottom: 'none !important',
    },
    '& .MuiInputBase-root:after': {
      borderBottom: 'none !important',
    },
  };

  /* =========================================================
     STATES
  ========================================================= */

  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  const [students, setStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const filtersOpen = Boolean(filterAnchorEl);

  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');

  const [courses, setCourses] = useState([]);
  const [filteredBatches, setFilteredBatches] = useState([]);

  const [organizationId, setOrganizationId] = useState(null);

  const [attendanceMap, setAttendanceMap] = useState({});
  const [localAttendance, setLocalAttendance] = useState({});
  const [rowDetails, setRowDetails] = useState({});

  /* =========================================================
     NEW: CHECKBOX SELECTION
  ========================================================= */

  const [selectedStudents, setSelectedStudents] = useState([]);

  const [globalMark, setGlobalMark] = useState('');

  const [globalTimeIn, setGlobalTimeIn] = useState('');
  const [globalTimeInPeriod, setGlobalTimeInPeriod] =
    useState('AM');

  const [globalTimeOut, setGlobalTimeOut] = useState('');
  const [globalTimeOutPeriod, setGlobalTimeOutPeriod] =
    useState('PM');

  const [globalRemark, setGlobalRemark] = useState('');

  const [showUserGuideModal, setShowUserGuideModal] =
    useState(false);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] =
    useState('success');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [detailModalOpen, setDetailModalOpen] =
    useState(false);

  const [detailRecord, setDetailRecord] = useState(null);

  const [detailLoading, setDetailLoading] =
    useState(false);

  /* =========================================================
     ORGANIZATION
  ========================================================= */

  const getOrganizationId = useCallback(() => {
    const user = safeParseJSON(
      sessionStorage.getItem('user')
    );

    if (user?.organization_id) {
      return user.organization_id;
    }

    const clientData = safeParseJSON(
      localStorage.getItem('client'),
      null
    );

    return clientData?.id || null;
  }, []);

  const showNotification = useCallback(
    (message, severity = 'success') => {
      setSnackbarMessage(message);
      setSnackbarSeverity(severity);
      setOpenSnackbar(true);
    },
    []
  );

  useEffect(() => {
    const orgId = getOrganizationId();

    if (orgId) {
      setOrganizationId(orgId);
    }
  }, [getOrganizationId]);

  /* =========================================================
     COURSE HELPERS
  ========================================================= */

  const getCourseOrganizationId = (course) => {
    const organization = course?.organization;

    return (
      organization?.id ??
      course?.organization_id ??
      organization ??
      ''
    );
  };

  const getCourseBranchId = (course) => {
    const branch = course?.branch;

    if (branch && typeof branch === 'object') {
      return (
        branch?.id ??
        branch?.branch_id ??
        ''
      );
    }

    return (
      course?.branch_id ??
      branch ??
      ''
    );
  };

  /* =========================================================
     FETCH COURSES
  ========================================================= */

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const token = getAuthToken();

        const params = {};

        if (organizationId) {
          params.organization_id = organizationId;
        }

        if (selectedBranchId) {
          params.branch_id = selectedBranchId;
        }

        const response = await axios.get(
          `${API_BASE_URL}/api/course/courses/`,
          {
            params,
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const rawCourses =
          response.data?.data ||
          response.data?.results ||
          response.data ||
          [];

        const list = Array.isArray(rawCourses)
          ? rawCourses
          : [];

        const filtered = list.filter((course) => {
          if (course?.is_active === false) {
            return false;
          }

          if (organizationId) {
            const courseOrgId =
              getCourseOrganizationId(course);

            if (
              courseOrgId !== '' &&
              String(courseOrgId) !==
                String(organizationId)
            ) {
              return false;
            }
          }

          if (!selectedBranchId) {
            return true;
          }

          const branchId =
            getCourseBranchId(course);

          if (branchId !== '') {
            return (
              String(branchId) ===
              String(selectedBranchId)
            );
          }

          return true;
        });

        setCourses(filtered);

        if (
          selectedCourse &&
          !filtered.some(
            (course) =>
              String(course.id) ===
              String(selectedCourse)
          )
        ) {
          setSelectedCourse('');
          setSelectedBatch('');
          setFilteredBatches([]);
        }
      } catch (error) {
        console.error(
          'Failed to fetch courses:',
          error
        );

        setCourses([]);
        setSelectedCourse('');
        setSelectedBatch('');
        setFilteredBatches([]);
      }
    };

    fetchCourses();
  }, [
    organizationId,
    selectedBranchId,
    selectedCourse,
  ]);

  /* =========================================================
     FETCH BATCHES
  ========================================================= */

  useEffect(() => {
    const fetchBatches = async () => {
      try {
        const token = getAuthToken();

        const params = selectedBranchId
          ? { branch_id: selectedBranchId }
          : {};

        let batchesData = [];

        if (selectedCourse) {
          const response = await axios.get(
            `${API_BASE_URL}/api/course/courses/${selectedCourse}/batches/`,
            {
              params,
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          batchesData =
            response.data?.batches ||
            response.data?.results ||
            response.data?.data ||
            response.data ||
            [];
        } else {
          const response = await axios.get(
            `${API_BASE_URL}/api/course/batches/`,
            {
              params,
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          batchesData =
            response.data?.results ||
            response.data?.data ||
            response.data ||
            [];
        }

        batchesData = Array.isArray(batchesData)
          ? batchesData
          : [];

        setFilteredBatches(batchesData);

        if (
          selectedBatch &&
          !batchesData.some(
            (b) =>
              String(b.id) ===
              String(selectedBatch)
          )
        ) {
          setSelectedBatch('');
        }
      } catch (error) {
        console.error(
          'Failed to fetch batches:',
          error
        );

        setFilteredBatches([]);
      }
    };

    fetchBatches();
  }, [selectedCourse, selectedBranchId]);

  /* =========================================================
     FETCH STUDENTS
  ========================================================= */

  useEffect(() => {
    const fetchStudents = async () => {
      setLoading(true);

      try {
        const token = getAuthToken();

        let url =
          `${API_BASE_URL}/api/admission/admissions/`;

        if (selectedCourse && selectedBatch) {
          url =
            `${API_BASE_URL}/api/admission/admission/course-batch/?course_id=${encodeURIComponent(
              selectedCourse
            )}&batch_id=${encodeURIComponent(
              selectedBatch
            )}`;

          if (selectedBranchId) {
            url += `&branch_id=${encodeURIComponent(
              selectedBranchId
            )}`;
          }
        } else if (selectedCourse) {
          url =
            `${API_BASE_URL}/api/admission/admission/course-batch/?course_id=${encodeURIComponent(
              selectedCourse
            )}`;

          if (selectedBranchId) {
            url += `&branch_id=${encodeURIComponent(
              selectedBranchId
            )}`;
          }
        } else if (selectedBatch) {
          url =
            `${API_BASE_URL}/api/admission/admission/course-batch/?batch_id=${encodeURIComponent(
              selectedBatch
            )}`;

          if (selectedBranchId) {
            url += `&branch_id=${encodeURIComponent(
              selectedBranchId
            )}`;
          }
        } else if (selectedBranchId) {
          url += `?branch_id=${encodeURIComponent(
            selectedBranchId
          )}`;
        }

        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const admissionsData =
          response.data?.results ||
          response.data?.data ||
          response.data ||
          [];

        const list = Array.isArray(admissionsData)
          ? admissionsData
          : [];

        const mappedStudents = list.map(
          (admission) => {
            const courseNames =
              admission.courses
                ?.map((c) => c.course_name)
                .filter(Boolean)
                .join(', ') || '-';

            const batchNames = [];

            admission.courses?.forEach(
              (course) => {
                course.batches?.forEach(
                  (batch) => {
                    const name =
                      batch.batch_name ||
                      batch.name;

                    if (
                      name &&
                      !batchNames.includes(name)
                    ) {
                      batchNames.push(name);
                    }
                  }
                );
              }
            );

            return {
              id: admission.id,
              AdmissionNo:
                admission.admission_code,
              Name:
                admission.candidate_name,
              course_name: courseNames,
              batch_name:
                batchNames.join(', ') || '-',
            };
          }
        );

        setStudents(mappedStudents);
      } catch (error) {
        console.error(
          'Failed to fetch admissions:',
          error
        );

        showNotification(
          'Failed to fetch students',
          'error'
        );

        setStudents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, [
    selectedCourse,
    selectedBatch,
    selectedBranchId,
    showNotification,
  ]);

  /* =========================================================
     FETCH EXISTING ATTENDANCE
  ========================================================= */

  useEffect(() => {
    if (!selectedDate) return;

    const fetchAttendance = async () => {
      try {
        const token = getAuthToken();
        const orgId = getOrganizationId();

        if (!token) return;

        const params = new URLSearchParams();

        params.append('start_date', selectedDate);
        params.append('end_date', selectedDate);

        if (orgId) {
          params.append('organization', orgId);
        }

        if (selectedBranchId) {
          params.append('branch', selectedBranchId);
        }

        const response = await axios.get(
          `${API_BASE_URL}/api/admission/attendance/?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        const records = Array.isArray(
          response.data
        )
          ? response.data
          : Array.isArray(
              response.data?.results
            )
          ? response.data.results
          : Array.isArray(
              response.data?.data
            )
          ? response.data.data
          : [];

        const map = {};
        const local = {};
        const details = {};
        const existingSelected = [];

        records.forEach((item) => {
          const admission =
            item.admission ?? item.admission_id;

          if (!admission) return;

          const timeIn =
            item.time_in || '';

          const timeOut =
            item.time_out || '';

          map[admission] = {
            status: item.status,
            recordId: item.id,
            time_in: timeIn,
            time_out: timeOut,
            remark: item.remark || '',
          };

          local[admission] =
            item.status;

          details[admission] = {
            time_in: timeIn,
            time_out: timeOut,
            time_in_period:
              getPeriodFromTime(
                timeIn,
                'AM'
              ),
            time_out_period:
              getPeriodFromTime(
                timeOut,
                'PM'
              ),
            remark: item.remark || '',
          };

          existingSelected.push(
            Number(admission)
          );
        });

        setAttendanceMap(map);
        setLocalAttendance(local);
        setRowDetails(details);

        /*
         * Existing saved attendance is automatically
         * selected so it can be edited/updated.
         */
        setSelectedStudents(
          existingSelected
        );

        setGlobalMark('');
        setGlobalTimeIn('');
        setGlobalTimeInPeriod('AM');
        setGlobalTimeOut('');
        setGlobalTimeOutPeriod('PM');
        setGlobalRemark('');
      } catch (error) {
        console.error(
          'Attendance API Error:',
          error?.response?.status,
          error?.response?.data || error
        );

        setAttendanceMap({});
        setLocalAttendance({});
        setRowDetails({});
        setSelectedStudents([]);

        setGlobalMark('');
        setGlobalTimeIn('');
        setGlobalTimeOut('');
        setGlobalRemark('');

        if (
          error?.response?.status === 401
        ) {
          showNotification(
            'Attendance authentication failed. Please login again.',
            'error'
          );
        } else if (
          error?.response?.status >= 500
        ) {
          showNotification(
            'Attendance server is temporarily unavailable.',
            'error'
          );
        }
      }
    };

    fetchAttendance();
  }, [
    selectedDate,
    selectedBranchId,
    getOrganizationId,
    showNotification,
  ]);

  /* =========================================================
     FILTERED STUDENTS
  ========================================================= */

  const filteredStudents = students.filter(
    (student) => {
      if (role === 'student') {
        return (
          Number(student.id) ===
          Number(
            currentUser?.admission_details
              ?.admission_id
          )
        );
      }

      const query =
        searchQuery.trim().toLowerCase();

      if (!query) return true;

      return (
        String(
          student.AdmissionNo || ''
        )
          .toLowerCase()
          .includes(query) ||
        String(student.Name || '')
          .toLowerCase()
          .includes(query)
      );
    }
  );

  /* =========================================================
     CHECKBOX HELPERS
  ========================================================= */

  const visibleStudentIds =
    filteredStudents.map((student) =>
      Number(student.id)
    );

  const selectedVisibleCount =
    visibleStudentIds.filter((id) =>
      selectedStudents.includes(id)
    ).length;

  const allVisibleSelected =
    filteredStudents.length > 0 &&
    selectedVisibleCount ===
      filteredStudents.length;

  const someVisibleSelected =
    selectedVisibleCount > 0 &&
    selectedVisibleCount <
      filteredStudents.length;

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedStudents((prev) => {
        const merged = new Set([
          ...prev,
          ...visibleStudentIds,
        ]);

        return Array.from(merged);
      });
    } else {
      setSelectedStudents((prev) =>
        prev.filter(
          (id) =>
            !visibleStudentIds.includes(id)
        )
      );
    }
  };

  const handleSelectStudent = (
    studentId,
    checked
  ) => {
    const id = Number(studentId);

    setSelectedStudents((prev) => {
      if (checked) {
        if (prev.includes(id)) {
          return prev;
        }

        return [...prev, id];
      }

      return prev.filter(
        (item) => item !== id
      );
    });
  };

  /* =========================================================
     GLOBAL ATTENDANCE
  ========================================================= */

  const handleGlobalMark = (value) => {
    setGlobalMark(value);

    /*
     * Apply only to CHECKED students.
     */
    setLocalAttendance((prev) => {
      const updated = { ...prev };

      selectedStudents.forEach((studentId) => {
        updated[studentId] = value;
      });

      return updated;
    });
  };

  /* =========================================================
     GLOBAL TIME
  ========================================================= */

  const applyGlobalTime = (
    value,
    field,
    period,
    setValue
  ) => {
    const normalizedInput =
      normalize12Input(value);

    setValue(normalizedInput);

    const trimmed =
      String(normalizedInput || '').trim();

    if (!trimmed) {
      setRowDetails((prev) => {
        const updated = { ...prev };

        selectedStudents.forEach(
          (studentId) => {
            updated[studentId] = {
              ...(updated[studentId] || {}),
              [field]: '',
              [`${field}_period`]: period,
            };
          }
        );

        return updated;
      });

      return;
    }

    let timeForParsing = trimmed;

    if (
      /^\d{1,2}$/.test(
        timeForParsing
      )
    ) {
      timeForParsing = `${timeForParsing}:00`;
    }

    const parsedTime =
      parseFixedPeriodTime(
        timeForParsing,
        period
      );

    if (!parsedTime) return;

    setRowDetails((prev) => {
      const updated = { ...prev };

      selectedStudents.forEach(
        (studentId) => {
          updated[studentId] = {
            ...(updated[studentId] || {}),
            [field]: parsedTime,
            [`${field}_period`]: period,
          };
        }
      );

      return updated;
    });
  };

  const handleGlobalTimeInChange = (
    value
  ) => {
    applyGlobalTime(
      value,
      'time_in',
      globalTimeInPeriod,
      setGlobalTimeIn
    );
  };

  const handleGlobalTimeInPeriodChange =
    (period) => {
      setGlobalTimeInPeriod(period);

      if (globalTimeIn) {
        applyGlobalTime(
          globalTimeIn,
          'time_in',
          period,
          setGlobalTimeIn
        );
      }
    };

  const handleGlobalTimeOutChange = (
    value
  ) => {
    applyGlobalTime(
      value,
      'time_out',
      globalTimeOutPeriod,
      setGlobalTimeOut
    );
  };

  const handleGlobalTimeOutPeriodChange =
    (period) => {
      setGlobalTimeOutPeriod(period);

      if (globalTimeOut) {
        applyGlobalTime(
          globalTimeOut,
          'time_out',
          period,
          setGlobalTimeOut
        );
      }
    };

  /* =========================================================
     GLOBAL REMARK
  ========================================================= */

  const handleGlobalRemarkChange = (
    value
  ) => {
    setGlobalRemark(value);

    setRowDetails((prev) => {
      const updated = { ...prev };

      selectedStudents.forEach(
        (studentId) => {
          updated[studentId] = {
            ...(updated[studentId] || {}),
            remark: value,
          };
        }
      );

      return updated;
    });
  };

  /* =========================================================
     ROW STATUS
  ========================================================= */

  const handleRowMark = (
    studentId,
    status
  ) => {
    const id = Number(studentId);

    setLocalAttendance((prev) => ({
      ...prev,
      [id]: status,
    }));

    /*
     * Automatically select the student when
     * attendance is marked individually.
     */
    setSelectedStudents((prev) => {
      if (prev.includes(id)) {
        return prev;
      }

      return [...prev, id];
    });

    setGlobalMark('');
  };

  /* =========================================================
     ROW TIME / REMARK
  ========================================================= */

  const handleRowDetailChange = (
    studentId,
    field,
    value
  ) => {
    const id = Number(studentId);

    const current =
      rowDetails[id] || {};

    const next = {
      ...current,
      [field]: value,
    };

    if (
      field === 'time_in' &&
      value
    ) {
      next.time_in_period =
        getPeriodFromTime(
          value,
          current.time_in_period ||
            'AM'
        );
    }

    if (
      field === 'time_out' &&
      value
    ) {
      next.time_out_period =
        getPeriodFromTime(
          value,
          current.time_out_period ||
            'PM'
        );
    }

    /*
     * Time Out is optional.
     * If both are present, validate order.
     */
    if (
      next.time_in &&
      next.time_out
    ) {
      const timeIn =
        timeToMinutes(
          normalizeTime(
            next.time_in
          )
        );

      const timeOut =
        timeToMinutes(
          normalizeTime(
            next.time_out
          )
        );

      if (
        timeIn !== null &&
        timeOut !== null &&
        timeOut <= timeIn
      ) {
        const student =
          students.find(
            (item) =>
              Number(item.id) === id
          );

        showNotification(
          `Time Out must be greater than Time In for ${
            student?.Name ||
            'this student'
          }.`,
          'error'
        );

        return;
      }
    }

    setRowDetails((prev) => ({
      ...prev,
      [id]: next,
    }));
  };

  /* =========================================================
     ROW PERIOD CHANGE
  ========================================================= */

  const handleRowPeriodChange = (
    studentId,
    field,
    period
  ) => {
    const id = Number(studentId);

    const current =
      rowDetails[id] || {};

    const timeValue =
      current[field] || '';

    const next = {
      ...current,
      [`${field}_period`]: period,
    };

    if (timeValue) {
      const inputValue =
        formatAmPmTime(
          timeValue
        );

      const timeOnly =
        inputValue.split(' ')[0];

      const parsedTime =
        parseFixedPeriodTime(
          timeOnly,
          period
        );

      if (parsedTime) {
        next[field] = parsedTime;
      }
    }

    /*
     * Validate Time In / Time Out after changing AM/PM.
     */
    if (
      next.time_in &&
      next.time_out
    ) {
      const timeIn =
        timeToMinutes(
          normalizeTime(
            next.time_in
          )
        );

      const timeOut =
        timeToMinutes(
          normalizeTime(
            next.time_out
          )
        );

      if (
        timeIn !== null &&
        timeOut !== null &&
        timeOut <= timeIn
      ) {
        const student =
          students.find(
            (item) =>
              Number(item.id) === id
          );

        showNotification(
          `Time Out must be greater than Time In for ${
            student?.Name ||
            'this student'
          }.`,
          'error'
        );

        return;
      }
    }

    setRowDetails((prev) => ({
      ...prev,
      [id]: next,
    }));
  };

  /* =========================================================
     CLEAR ATTENDANCE
  ========================================================= */

  const handleClearAttendance = async (
    studentId
  ) => {
    const id = Number(studentId);

    const existing =
      attendanceMap[id];

    if (!existing) {
      setLocalAttendance(
        (prev) => {
          const x = { ...prev };
          delete x[id];
          return x;
        }
      );

      setRowDetails(
        (prev) => {
          const x = { ...prev };
          delete x[id];
          return x;
        }
      );

      setSelectedStudents(
        (prev) =>
          prev.filter(
            (item) => item !== id
          )
      );

      return;
    }

    try {
      await axios.delete(
        `${API_BASE_URL}/api/admission/attendance/${existing.recordId}/`,
        {
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
          },
        }
      );

      setAttendanceMap(
        (prev) => {
          const x = { ...prev };
          delete x[id];
          return x;
        }
      );

      setLocalAttendance(
        (prev) => {
          const x = { ...prev };
          delete x[id];
          return x;
        }
      );

      setRowDetails(
        (prev) => {
          const x = { ...prev };
          delete x[id];
          return x;
        }
      );

      setSelectedStudents(
        (prev) =>
          prev.filter(
            (item) => item !== id
          )
      );

      showNotification(
        'Attendance record deleted'
      );
    } catch {
      showNotification(
        'Failed to delete attendance record',
        'error'
      );
    }
  };

  /* =========================================================
     DETAIL
  ========================================================= */

  const fetchAttendanceDetail = async (
    recordId
  ) => {
    setDetailLoading(true);
    setDetailModalOpen(true);

    try {
      const response =
        await axios.get(
          `${API_BASE_URL}/api/admission/attendance/${recordId}/`,
          {
            headers: {
              Authorization: `Bearer ${getAuthToken()}`,
            },
          }
        );

      setDetailRecord(
        response.data
      );
    } catch (error) {
      console.error(
        'Failed to fetch attendance detail:',
        error
      );

      showNotification(
        'Failed to fetch attendance record detail',
        'error'
      );

      setDetailModalOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  /* =========================================================
     SAVE
  ========================================================= */

  const handleSave = async () => {
    const marked =
      Object.entries(
        localAttendance
      );

    if (!marked.length) {
      showNotification(
        'Please mark attendance for at least one student',
        'error'
      );

      return;
    }

    /*
     * =====================================================
     * MANDATORY TIME IN VALIDATION
     * =====================================================
     *
     * Every student whose attendance is marked
     * MUST have Time In.
     *
     * Time Out remains optional.
     */
    for (
      const [studentId, status] of marked
    ) {
      const details =
        rowDetails[
          Number(studentId)
        ] || {};

      if (!details.time_in) {
        const student =
          students.find(
            (s) =>
              Number(s.id) ===
              Number(studentId)
          );

        showNotification(
          `Time In is mandatory for ${
            student?.Name ||
            'this student'
          }.`,
          'error'
        );

        /*
         * Automatically select the student
         * so the user knows which row needs attention.
         */
        setSelectedStudents(
          (prev) => {
            const id =
              Number(studentId);

            if (prev.includes(id)) {
              return prev;
            }

            return [...prev, id];
          }
        );

        return;
      }

      /*
       * Validate Time In.
       */
      const timeIn =
        timeToMinutes(
          normalizeTime(
            details.time_in
          )
        );

      if (timeIn === null) {
        const student =
          students.find(
            (s) =>
              Number(s.id) ===
              Number(studentId)
          );

        showNotification(
          `Please enter a valid Time In for ${
            student?.Name ||
            'this student'
          }.`,
          'error'
        );

        return;
      }

      /*
       * Time Out is optional.
       * But if entered, it must be valid and
       * greater than Time In.
       */
      if (details.time_out) {
        const timeOut =
          timeToMinutes(
            normalizeTime(
              details.time_out
            )
          );

        if (timeOut === null) {
          showNotification(
            'Please enter a valid Time Out value.',
            'error'
          );

          return;
        }

        if (timeOut <= timeIn) {
          const student =
            students.find(
              (s) =>
                Number(s.id) ===
                Number(studentId)
            );

          showNotification(
            `Time Out must be greater than Time In for ${
              student?.Name ||
              'this student'
            }.`,
            'error'
          );

          return;
        }
      }
    }

    setSaving(true);

    try {
      const headers = {
        Authorization: `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json',
      };

      const updatedMap = {
        ...attendanceMap,
      };

      const updatedDetails = {
        ...rowDetails,
      };

      for (
        const [studentId, status] of marked
      ) {
        const id =
          Number(studentId);

        const existing =
          attendanceMap[id];

        const details =
          rowDetails[id] || {};

        const payload = {
          admission: id,
          date: selectedDate,
          status,

          /*
           * Backend receives 24-hour format.
           */
          time_in: details.time_in
            ? normalizeTime(
                details.time_in
              )
            : null,

          time_out: details.time_out
            ? normalizeTime(
                details.time_out
              )
            : null,

          remark:
            details.remark || '',
        };

        let response;

        if (existing?.recordId) {
          response =
            await axios.put(
              `${API_BASE_URL}/api/admission/attendance/${existing.recordId}/`,
              payload,
              { headers }
            );
        } else {
          try {
            response =
              await axios.post(
                `${API_BASE_URL}/api/admission/attendance/`,
                payload,
                { headers }
              );
          } catch (postError) {
            const data =
              postError?.response?.data;

            if (
              postError?.response
                ?.status === 400 &&
              data?.non_field_errors?.some(
                (msg) =>
                  String(msg).includes(
                    'admission, date'
                  )
              )
            ) {
              const existingResponse =
                await axios.get(
                  `${API_BASE_URL}/api/admission/attendance/?admission_id=${id}&start_date=${selectedDate}&end_date=${selectedDate}`,
                  { headers }
                );

              const existingRecords =
                Array.isArray(
                  existingResponse.data
                )
                  ? existingResponse.data
                  : existingResponse.data
                      ?.results ||
                    existingResponse.data
                      ?.data ||
                    [];

              const record =
                existingRecords.find(
                  (r) =>
                    Number(r.admission) ===
                      id &&
                    r.date ===
                      selectedDate
                );

              if (!record?.id) {
                throw postError;
              }

              response =
                await axios.put(
                  `${API_BASE_URL}/api/admission/attendance/${record.id}/`,
                  payload,
                  { headers }
                );
            } else {
              throw postError;
            }
          }
        }

        const saved =
          response.data;

        updatedMap[id] = {
          status: saved.status,
          recordId: saved.id,
          time_in:
            saved.time_in || '',
          time_out:
            saved.time_out || '',
          remark:
            saved.remark || '',
        };

        updatedDetails[id] = {
          time_in:
            saved.time_in || '',
          time_out:
            saved.time_out || '',
          time_in_period:
            getPeriodFromTime(
              saved.time_in,
              'AM'
            ),
          time_out_period:
            getPeriodFromTime(
              saved.time_out,
              'PM'
            ),
          remark:
            saved.remark || '',
        };
      }

      setAttendanceMap(
        updatedMap
      );

      setRowDetails(
        updatedDetails
      );

      showNotification(
        'Attendance saved successfully!',
        'success'
      );
    } catch (error) {
      console.error(
        'Failed to save attendance:',
        error?.response?.data ||
          error
      );

      const data =
        error?.response?.data;

      const message =
        data?.non_field_errors?.[0] ||
        data?.time_out?.[0] ||
        data?.time_in?.[0] ||
        data?.detail ||
        'Failed to save attendance';

      showNotification(
        message,
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     COUNTS
  ========================================================= */

  const markedCount =
    Object.keys(
      localAttendance
    ).length;

  const presentCount =
    Object.values(
      localAttendance
    ).filter(
      (v) => v === 'present'
    ).length;

  const absentCount =
    Object.values(
      localAttendance
    ).filter(
      (v) => v === 'absent'
    ).length;

  const halfDayCount =
    Object.values(
      localAttendance
    ).filter(
      (v) => v === 'half_day'
    ).length;

  const onLeaveCount =
    Object.values(
      localAttendance
    ).filter(
      (v) => v === 'on_leave'
    ).length;

  const unmarkedCount =
    Math.max(
      0,
      students.length -
        markedCount
    );

  /* =========================================================
     BRANCH CHANGE RESET
  ========================================================= */

  useEffect(() => {
    setSelectedCourse('');
    setSelectedBatch('');
    setFilteredBatches([]);
    setLocalAttendance({});
    setRowDetails({});
    setSelectedStudents([]);
    setGlobalMark('');
    setGlobalTimeIn('');
    setGlobalTimeOut('');
    setGlobalRemark('');
  }, [selectedBranchId]);

  /* =========================================================
     COURSE CHANGE
  ========================================================= */

  const handleCourseChange = (e) => {
    setSelectedCourse(
      String(e.target.value ?? '')
    );

    setSelectedBatch('');
    setFilteredBatches([]);

    setSelectedStudents([]);
    setLocalAttendance({});
    setRowDetails({});
  };

  const handleBackToAttendance = () => {
    navigate('/student-attendance');
  };

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <Container
      sx={{
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* =====================================================
          HEADER
      ===================================================== */}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          pt: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
         

   

          {/* SEARCH - remains visible */}
        <TextField
          sx={{
            width: { xs: '100%', sm: 300 },
            flex: { sm: 1 },
            '& .MuiInputLabel-root': {
              color:
                theme.palette.mode === 'dark'
                  ? '#fff'
                  : theme.palette.text.primary,
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: theme.palette.primary.main,
            },
            '& .MuiInputBase-input': {
              color:
                theme.palette.mode === 'dark'
                  ? '#fff'
                  : theme.palette.text.primary,
            },
            '& .MuiInputBase-input::placeholder': {
              color:
                theme.palette.mode === 'dark'
                  ? '#fff'
                  : theme.palette.text.disabled,
              opacity: 1,
            },
            '& .MuiInputAdornment-root .MuiSvgIcon-root': {
              color:
                theme.palette.mode === 'dark'
                  ? '#fff'
                  : theme.palette.text.disabled,
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: theme.palette.divider,
              },
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
          label="Search students"
          placeholder="Search by name or admission no"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
 <Box gap={1} sx={{ display: 'flex', alignItems: 'center' }}>
        {/* FILTER BUTTON */}
        <Button
          variant="contained"
          startIcon={<FilterListIcon />}
          onClick={(event) => setFilterAnchorEl(event.currentTarget)}
          sx={{
            height: 40,
            px: 2,
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          Filter
        </Button>

        {/* FILTER POPOVER */}
        <Popover
          open={filtersOpen}
          anchorEl={filterAnchorEl}
          onClose={() => setFilterAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              mt: 1,
              p: 2,
              width: { xs: 'calc(100vw - 32px)', sm: 320 },
              maxWidth: 'calc(100vw - 32px)',
              borderRadius: 3,
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[8],
            },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Filter Attendance
            </Typography>

            <TextField
              fullWidth
              size="small"
              type="date"
              label="Attendance Date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={labelSx}
            />

            <FormControl fullWidth size="small" sx={labelSx}>
              <InputLabel>Course</InputLabel>
              <Select
                value={selectedCourse}
                onChange={handleCourseChange}
                label="Course"
              >
                <MenuItem value=""><em>All Courses</em></MenuItem>
                {courses.map((course) => (
                  <MenuItem key={course.id} value={String(course.id)}>
                    {course.course_name || course.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" sx={labelSx}>
              <InputLabel>Batch</InputLabel>
              <Select
                value={selectedBatch}
                onChange={(e) =>
                  setSelectedBatch(String(e.target.value ?? ''))
                }
                label="Batch"
              >
                <MenuItem value=""><em>All Batches</em></MenuItem>
                {filteredBatches.length > 0 ? (
                  filteredBatches.map((batch) => (
                    <MenuItem key={batch.id} value={String(batch.id)}>
                      {batch.batch_name || batch.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    {selectedCourse
                      ? 'No batches available for this course'
                      : 'No batches available'}
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                size="small"
                onClick={() => {
                  setSelectedDate(new Date().toISOString().split('T')[0]);
                  setSelectedCourse('');
                  setSelectedBatch('');
                }}
              >
                Reset
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => setFilterAnchorEl(null)}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>

        <IconButton
          onClick={() =>
            setShowUserGuideModal(true)
          }
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
          }}
        >
          <InfoOutlineIcon
            sx={{ fontSize: 26 }}
          />
        </IconButton>
      </Box>


          </Box>

        </Box>
      {/* =====================================================
          GLOBAL ATTENDANCE
      ===================================================== */}

      {students.length > 0 &&
        !loading && (
          <Paper
            elevation={0}
            sx={{
              px: 3,
              py: 2,
              mb: 2,
              borderRadius: '12px',
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <PeopleAltIcon
                  sx={{
                    color: 'primary.main',
                    fontSize: 20,
                  }}
                />

                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                >
                  Global Attendance
                </Typography>

                <Chip
                  size="small"
                  variant="outlined"
                  label={`${selectedStudents.length} selected`}
                />
              </Box>

              {/* GLOBAL STATUS */}

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                >
                  Mark Selected As:
                </Typography>

                <ToggleButtonGroup
                  value={globalMark}
                  exclusive
                  size="small"
                  onChange={(e, value) => {
                    if (value !== null) {
                      handleGlobalMark(
                        value
                      );
                    }
                  }}
                >
                  {STATUS_OPTIONS.map(
                    (opt) => {
                      const paletteColor =
                        theme.palette[
                          opt.color
                        ] ||
                        theme.palette
                          .primary;

                      return (
                        <ToggleButton
                          key={
                            opt.value
                          }
                          value={
                            opt.value
                          }
                          disabled={
                            selectedStudents.length ===
                            0
                          }
                          sx={{
                            minWidth: {
                              xs: 58,
                              sm: 72,
                            },
                            px: 1.2,
                            fontWeight: 700,
                            textTransform:
                              'none',
                            color:
                              paletteColor.main,
                            borderColor:
                              theme.palette
                                .divider,
                            '&:hover':
                              {
                                backgroundColor:
                                  paletteColor.light,
                                color:
                                  paletteColor.dark,
                              },
                            '&.Mui-selected':
                              {
                                backgroundColor:
                                  paletteColor.main,
                                color:
                                  '#fff',
                                borderColor:
                                  paletteColor.main,
                                '&:hover':
                                  {
                                    backgroundColor:
                                      paletteColor.dark,
                                    color:
                                      '#fff',
                                  },
                              },
                          }}
                        >
                          {
                            opt.label
                          }
                        </ToggleButton>
                      );
                    }
                  )}
                </ToggleButtonGroup>
              </Box>

              {/* GLOBAL TIME IN */}

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                }}
              >
                <TextField
                  label="Time In"
                  size="small"
                  value={
                    globalTimeIn
                  }
                  onChange={(e) =>
                    handleGlobalTimeInChange(
                      e.target.value
                    )
                  }
                  placeholder="11:00"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    inputMode:
                      'numeric',
                  }}
                  disabled={
                    selectedStudents.length ===
                    0
                  }
                  sx={{
                    width: 145,
                    ...noUnderlineSx,
                  }}
                />

                <Select
                  size="small"
                  value={
                    globalTimeInPeriod
                  }
                  onChange={(e) =>
                    handleGlobalTimeInPeriodChange(
                      e.target.value
                    )
                  }
                  disabled={
                    selectedStudents.length ===
                    0
                  }
                  sx={{
                    minWidth: 72,
                    height: 40,
                  }}
                >
                  <MenuItem value="AM">
                    AM
                  </MenuItem>

                  <MenuItem value="PM">
                    PM
                  </MenuItem>
                </Select>
              </Box>

              {/* GLOBAL TIME OUT */}

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                }}
              >
                <TextField
                  label="Time Out"
                  size="small"
                  value={
                    globalTimeOut
                  }
                  onChange={(e) =>
                    handleGlobalTimeOutChange(
                      e.target.value
                    )
                  }
                  placeholder="6:00"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    inputMode:
                      'numeric',
                  }}
                  disabled={
                    selectedStudents.length ===
                    0
                  }
                  sx={{
                    width: 145,
                    ...labelSx,
                  }}
                />

                <Select
                  size="small"
                  value={
                    globalTimeOutPeriod
                  }
                  onChange={(e) =>
                    handleGlobalTimeOutPeriodChange(
                      e.target.value
                    )
                  }
                  disabled={
                    selectedStudents.length ===
                    0
                  }
                  sx={{
                    minWidth: 72,
                    height: 40,
                  }}
                >
                  <MenuItem value="AM">
                    AM
                  </MenuItem>

                  <MenuItem value="PM">
                    PM
                  </MenuItem>
                </Select>
              </Box>

              {/* GLOBAL REMARK */}

              <TextField
                label="Global Remark"
                placeholder="Apply remark to selected"
                size="small"
                value={globalRemark}
                onChange={(e) =>
                  handleGlobalRemarkChange(
                    e.target.value
                  )
                }
                disabled={
                  selectedStudents.length ===
                  0
                }
                sx={{
                  width: {
                    xs: '100%',
                    sm: 220,
                  },
                  flex: 1,
                  minWidth: 180,
                  '& .MuiInputBase-input':
                    {
                      color:
                        theme.palette
                          .mode ===
                        'dark'
                          ? '#fff'
                          : theme.palette
                              .text.primary,
                    },
                  '& .MuiInputLabel-root':
                    {
                      color:
                        theme.palette
                          .mode ===
                        'dark'
                          ? '#fff'
                          : theme.palette
                              .text.primary,
                    },
                  '& .MuiInputLabel-root.Mui-focused':
                    {
                      color:
                        theme.palette
                          .primary
                          .main,
                    },
                  '& .MuiOutlinedInput-root':
                    {
                      '& fieldset': {
                        borderColor:
                          theme.palette
                            .divider,
                      },
                      '&:hover fieldset':
                        {
                          borderColor:
                            theme.palette
                              .text
                              .disabled,
                        },
                      '&.Mui-focused fieldset':
                        {
                          borderColor:
                            theme.palette
                              .primary
                              .main,
                        },
                    },
                  '& .MuiInputBase-input::placeholder':
                    {
                      color:
                        theme.palette
                          .mode ===
                        'dark'
                          ? '#fff'
                          : theme.palette
                              .text
                              .disabled,
                      opacity: 1,
                    },
                }}
              />

              {markedCount > 0 && (
                <>
                  <Divider
                    orientation="vertical"
                    flexItem
                  />

                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Chip
                      icon={
                        <CheckCircleOutlineIcon
                          sx={{
                            fontSize: 14,
                          }}
                        />
                      }
                      label={`${presentCount} Present`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />

                    <Chip
                      icon={
                        <HighlightOffIcon
                          sx={{
                            fontSize: 14,
                          }}
                        />
                      }
                      label={`${absentCount} Absent`}
                      size="small"
                      color="error"
                      variant="outlined"
                    />

                    {halfDayCount >
                      0 && (
                      <Chip
                        label={`${halfDayCount} Half Day`}
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    )}

                    {onLeaveCount >
                      0 && (
                      <Chip
                        label={`${onLeaveCount} On Leave`}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    )}

                    {unmarkedCount >
                      0 && (
                      <Chip
                        label={`${unmarkedCount} Unmarked`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Paper>
        )}

      {/* =====================================================
          TABLE
      ===================================================== */}

      <Paper
        elevation={2}
        sx={{
          maxWidth: '100%',
          boxSizing: 'border-box',
          mx: 'auto',
          backgroundColor:
            'background.paper',
          borderRadius: '16px',
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              p: 6,
            }}
          >
            <CircularProgress />
          </Box>
        ) : filteredStudents.length >
          0 ? (
          <Box
            sx={{
              width: '100%',
              overflowX: 'auto',
            }}
          >
            <TableContainer>
              <Table
                sx={{
                  minWidth: 1350,
                  '& .MuiTableHead-root .MuiTableCell-root':
                    {
                      fontWeight: 700,
                      backgroundColor:
                        theme.palette
                          .primary
                          .main,
                      color: '#fff',
                    },
                }}
              >
                <TableHead>
                  <TableRow>
                    {/* CHECKBOX */}

                    <TableCell
                      padding="checkbox"
                      sx={{
                        width: 55,
                      }}
                    >
                      <Checkbox
                        checked={
                          allVisibleSelected
                        }
                        indeterminate={
                          someVisibleSelected
                        }
                        onChange={
                          handleSelectAll
                        }
                        sx={{
                          color: '#fff',
                          '&.Mui-checked':
                            {
                              color: '#fff',
                            },
                          '&.MuiCheckbox-indeterminate':
                            {
                              color: '#fff',
                            },
                        }}
                      />
                    </TableCell>

                    <TableCell
                      sx={{
                        width: 60,
                      }}
                    >
                      S.No
                    </TableCell>

                    <TableCell>
                      Admission No
                    </TableCell>

                    <TableCell>
                      Candidate Name
                    </TableCell>

                    <TableCell>
                      Course
                    </TableCell>

                    <TableCell>
                      Batch
                    </TableCell>

                    <TableCell
                      sx={{
                        width: 190,
                      }}
                    >
                      Mark (P/A/H/L)
                    </TableCell>

                    <TableCell
                      sx={{
                        width: 230,
                      }}
                    >
                      Time In
                    </TableCell>

                    <TableCell
                      sx={{
                        width: 230,
                      }}
                    >
                      Time Out
                    </TableCell>

                    <TableCell
                      sx={{
                        width: 180,
                      }}
                    >
                      Remarks
                    </TableCell>

                    <TableCell
                      sx={{
                        width: 90,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredStudents.map(
                    (
                      student,
                      index
                    ) => {
                      const id =
                        Number(
                          student.id
                        );

                      const currentStatus =
                        localAttendance[
                          id
                        ] || null;

                      const hasExistingRecord =
                        !!attendanceMap[
                          id
                        ];

                      const isSelected =
                        selectedStudents.includes(
                          id
                        );

                      const details =
                        rowDetails[id] ||
                        {
                          time_in: '',
                          time_out: '',
                          time_in_period:
                            'AM',
                          time_out_period:
                            'PM',
                          remark: '',
                        };

                      const currentInPeriod =
                        details.time_in_period ||
                        getPeriodFromTime(
                          details.time_in,
                          'AM'
                        );

                      const currentOutPeriod =
                        details.time_out_period ||
                        getPeriodFromTime(
                          details.time_out,
                          'PM'
                        );

                      return (
                        <TableRow
                          key={
                            student.id
                          }
                          selected={
                            isSelected
                          }
                          sx={{
                            '&.Mui-selected':
                              {
                                backgroundColor:
                                  theme
                                    .palette
                                    .action
                                    .selected,
                              },
                          }}
                        >
                          {/* ROW CHECKBOX */}

                          <TableCell
                            padding="checkbox"
                          >
                            <Checkbox
                              checked={
                                isSelected
                              }
                              onChange={(
                                e
                              ) =>
                                handleSelectStudent(
                                  id,
                                  e
                                    .target
                                    .checked
                                )
                              }
                            />
                          </TableCell>

                          {/* S.NO */}

                          <TableCell>
                            {index + 1}
                          </TableCell>

                          {/* ADMISSION */}

                          <TableCell
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            {
                              student.AdmissionNo
                            }
                          </TableCell>

                          {/* NAME */}

                          <TableCell>
                            {
                              student.Name
                            }
                          </TableCell>

                          {/* COURSE */}

                          <TableCell>
                            {
                              student.course_name
                            }
                          </TableCell>

                          {/* BATCH */}

                          <TableCell>
                            {
                              student.batch_name
                            }
                          </TableCell>

                          {/* STATUS */}

                          <TableCell>
                            <ToggleButtonGroup
                              value={
                                currentStatus
                              }
                              exclusive
                              size="small"
                              onChange={(
                                e,
                                value
                              ) => {
                                if (
                                  value !==
                                  null
                                ) {
                                  handleRowMark(
                                    id,
                                    value
                                  );
                                }
                              }}
                            >
                              {STATUS_OPTIONS.map(
                                (
                                  opt
                                ) => {
                                  const paletteColor =
                                    theme
                                      .palette[
                                      opt
                                        .color
                                    ] ||
                                    theme
                                      .palette
                                      .primary;

                                  return (
                                    <ToggleButton
                                      key={
                                        opt.value
                                      }
                                      value={
                                        opt.value
                                      }
                                      sx={{
                                        minWidth: 36,
                                        fontWeight: 700,
                                        px: 1,
                                        color:
                                          paletteColor.main,
                                        borderColor:
                                          theme
                                            .palette
                                            .divider,
                                        '&:hover':
                                          {
                                            backgroundColor:
                                              paletteColor.light,
                                          },
                                        '&.Mui-selected':
                                          {
                                            backgroundColor:
                                              paletteColor.main,
                                            color:
                                              '#fff',
                                            borderColor:
                                              paletteColor.main,
                                            '&:hover':
                                              {
                                                backgroundColor:
                                                  paletteColor.dark,
                                                color:
                                                  '#fff',
                                              },
                                          },
                                      }}
                                    >
                                      <Tooltip
                                        title={
                                          opt.label
                                        }
                                      >
                                        <span>
                                          {
                                            opt.shortLabel
                                          }
                                        </span>
                                      </Tooltip>
                                    </ToggleButton>
                                  );
                                }
                              )}
                            </ToggleButtonGroup>

                            {currentStatus &&
                              hasExistingRecord && (
                                <Tooltip title="Click to view saved record detail">
                                  <Chip
                                    label="saved"
                                    size="small"
                                    variant="outlined"
                                    onClick={() =>
                                      fetchAttendanceDetail(
                                        attendanceMap[
                                          id
                                        ]
                                          .recordId
                                      )
                                    }
                                    sx={{
                                      ml: 1,
                                      height: 20,
                                      fontSize:
                                        '0.65rem',
                                      cursor:
                                        'pointer',
                                    }}
                                  />
                                </Tooltip>
                              )}
                          </TableCell>

                          {/* =================================================
                              TIME IN + AM/PM
                          ================================================= */}

                          <TableCell>
                            <Box
                              sx={{
                                display:
                                  'flex',
                                alignItems:
                                  'center',
                                gap: 0.5,
                              }}
                            >
                              <TextField
                                size="small"
                                value={
                                  details.time_in
                                    ? formatAmPmTime(
                                        details.time_in
                                      ).split(
                                        ' '
                                      )[0]
                                    : ''
                                }
                                onChange={(
                                  e
                                ) => {
                                  const input =
                                    normalize12Input(
                                      e
                                        .target
                                        .value
                                    );

                                  if (
                                    !input
                                  ) {
                                    handleRowDetailChange(
                                      id,
                                      'time_in',
                                      ''
                                    );
                                    return;
                                  }

                                  let value =
                                    input;

                                  if (
                                    /^\d{1,2}$/.test(
                                      value
                                    )
                                  ) {
                                    value = `${value}:00`;
                                  }

                                  const parsed =
                                    parseFixedPeriodTime(
                                      value,
                                      currentInPeriod
                                    );

                                  if (
                                    parsed
                                  ) {
                                    handleRowDetailChange(
                                      id,
                                      'time_in',
                                      parsed
                                    );
                                  }
                                }}
                                placeholder="11:00"
                                inputProps={{
                                  inputMode:
                                    'numeric',
                                }}
                                sx={{
                                  width: 110,
                                  ...noUnderlineSx,
                                  '& .MuiInputBase-root':
                                    {
                                      height: 40,
                                    },
                                }}
                              />

                              <Select
                                size="small"
                                value={
                                  currentInPeriod
                                }
                                onChange={(
                                  e
                                ) =>
                                  handleRowPeriodChange(
                                    id,
                                    'time_in',
                                    e
                                      .target
                                      .value
                                  )
                                }
                                sx={{
                                  minWidth: 70,
                                  height: 40,
                                }}
                              >
                                <MenuItem value="AM">
                                  AM
                                </MenuItem>

                                <MenuItem value="PM">
                                  PM
                                </MenuItem>
                              </Select>

                              <Tooltip title="Set current time">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const now =
                                      new Date();

                                    const hours =
                                      now.getHours();

                                    const minutes =
                                      now.getMinutes();

                                    const period =
                                      hours >=
                                      12
                                        ? 'PM'
                                        : 'AM';

                                    let displayHours =
                                      hours %
                                        12 ||
                                      12;

                                    const value =
                                      `${String(
                                        displayHours
                                      ).padStart(
                                        2,
                                        '0'
                                      )}:${String(
                                        minutes
                                      ).padStart(
                                        2,
                                        '0'
                                      )}`;

                                    setRowDetails(
                                      (
                                        prev
                                      ) => ({
                                        ...prev,
                                        [id]:
                                          {
                                            ...(prev[
                                              id
                                            ] ||
                                              {}),
                                            time_in:
                                              `${String(
                                                hours
                                              ).padStart(
                                                2,
                                                '0'
                                              )}:${String(
                                                minutes
                                              ).padStart(
                                                2,
                                                '0'
                                              )}`,
                                            time_in_period:
                                              period,
                                          },
                                      })
                                    );
                                  }}
                                >
                                  <AccessTimeIcon
                                    sx={{
                                      fontSize: 17,
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>

                          {/* =================================================
                              TIME OUT + AM/PM
                          ================================================= */}

                          <TableCell>
                            <Box
                              sx={{
                                display:
                                  'flex',
                                alignItems:
                                  'center',
                                gap: 0.5,
                              }}
                            >
                              <TextField
                                size="small"
                                value={
                                  details.time_out
                                    ? formatAmPmTime(
                                        details.time_out
                                      ).split(
                                        ' '
                                      )[0]
                                    : ''
                                }
                                onChange={(
                                  e
                                ) => {
                                  const input =
                                    normalize12Input(
                                      e
                                        .target
                                        .value
                                    );

                                  if (
                                    !input
                                  ) {
                                    handleRowDetailChange(
                                      id,
                                      'time_out',
                                      ''
                                    );
                                    return;
                                  }

                                  let value =
                                    input;

                                  if (
                                    /^\d{1,2}$/.test(
                                      value
                                    )
                                  ) {
                                    value = `${value}:00`;
                                  }

                                  const parsed =
                                    parseFixedPeriodTime(
                                      value,
                                      currentOutPeriod
                                    );

                                  if (
                                    parsed
                                  ) {
                                    handleRowDetailChange(
                                      id,
                                      'time_out',
                                      parsed
                                    );
                                  }
                                }}
                                placeholder="06:00"
                                inputProps={{
                                  inputMode:
                                    'numeric',
                                }}
                                sx={{
                                  width: 110,
                                  ...noUnderlineSx,
                                  '& .MuiInputBase-root':
                                    {
                                      height: 40,
                                    },
                                }}
                              />

                              <Select
                                size="small"
                                value={
                                  currentOutPeriod
                                }
                                onChange={(
                                  e
                                ) =>
                                  handleRowPeriodChange(
                                    id,
                                    'time_out',
                                    e
                                      .target
                                      .value
                                  )
                                }
                                sx={{
                                  minWidth: 70,
                                  height: 40,
                                }}
                              >
                                <MenuItem value="AM">
                                  AM
                                </MenuItem>

                                <MenuItem value="PM">
                                  PM
                                </MenuItem>
                              </Select>

                              <Tooltip
                                title={
                                  details.time_out
                                    ? 'Clear time out'
                                    : ''
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={
                                      !details.time_out
                                    }
                                    onClick={() =>
                                      handleRowDetailChange(
                                        id,
                                        'time_out',
                                        ''
                                      )
                                    }
                                    sx={{
                                      visibility:
                                        details.time_out
                                          ? 'visible'
                                          : 'hidden',
                                    }}
                                  >
                                    <ClearIcon
                                      sx={{
                                        fontSize: 16,
                                      }}
                                    />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </TableCell>

                          {/* REMARK */}

                          <TableCell>
                            <TextField
                              size="small"
                              placeholder="Remark"
                              value={
                                details.remark ||
                                ''
                              }
                              onChange={(e) =>
                                handleRowDetailChange(
                                  id,
                                  'remark',
                                  e
                                    .target
                                    .value
                                )
                              }
                              sx={{
                                width: 160,
                              }}
                            />
                          </TableCell>

                          {/* ACTIONS */}

                          <TableCell align="center">
                            {currentStatus && (
                              <Tooltip
                                title={
                                  hasExistingRecord
                                    ? 'Delete saved record'
                                    : 'Clear mark'
                                }
                              >
                                <IconButton
                                  onClick={() =>
                                    handleClearAttendance(
                                      id
                                    )
                                  }
                                  size="small"
                                >
                                  <DeleteOutlineIcon
                                    sx={{
                                      fontSize: 18,
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    }
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              p: 6,
            }}
          >
            <Typography>
              No students found
            </Typography>
          </Box>
        )}
      </Paper>

      {/* =====================================================
          SAVE
      ===================================================== */}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 2,
          p: 2,
        }}
      >
        {markedCount > 0 && (
          <Typography variant="body2">
            {markedCount} of{' '}
            {students.length}{' '}
            student
            {students.length !==
            1
              ? 's'
              : ''}{' '}
            marked
          </Typography>
        )}

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            markedCount === 0 ||
            saving
          }
          startIcon={
            saving ? (
              <CircularProgress
                size={16}
                color="inherit"
              />
            ) : (
              <SaveIcon />
            )
          }
          sx={{
            fontWeight: 700,
            px: 4,
            py: 1,
            textTransform: 'none',
            borderRadius: '8px',
          }}
        >
          {saving
            ? 'Saving...'
            : 'Save Attendance'}
        </Button>
      </Box>

      {/* =====================================================
          SNACKBAR
      ===================================================== */}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() =>
          setOpenSnackbar(false)
        }
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert
          onClose={() =>
            setOpenSnackbar(false)
          }
          severity={
            snackbarSeverity
          }
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* =====================================================
          DETAIL MODAL
      ===================================================== */}

      <Modal
        open={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setDetailRecord(null);
        }}
        BackdropProps={{
          style: {
            backgroundColor:
              'rgba(0,0,0,0.75)',
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform:
              'translate(-50%, -50%)',
            width: {
              xs: '90%',
              sm: 500,
            },
            maxHeight: '85vh',
            bgcolor:
              'background.paper',
            borderRadius: '16px',
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor:
                'primary.main',
              p: '14px 20px',
              display: 'flex',
              justifyContent:
                'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
                fontWeight: 700,
              }}
            >
              Attendance Record{' '}
              {detailRecord?.id
                ? `#${detailRecord.id}`
                : ''}
            </Typography>

            <IconButton
              onClick={() => {
                setDetailModalOpen(
                  false
                );
                setDetailRecord(
                  null
                );
              }}
              sx={{
                color: '#fff',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <CustomTableScrollbar
            sx={{
              overflowY: 'auto',
              p: 3,
            }}
          >
            {detailLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent:
                    'center',
                  py: 6,
                }}
              >
                <CircularProgress />
              </Box>
            ) : detailRecord ? (
              <Grid
                container
                spacing={2.5}
              >
                <Grid item xs={6}>
                  <Typography variant="body2">
                    Record ID
                  </Typography>

                  <Typography fontWeight={600}>
                    {detailRecord.id ??
                      '—'}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2">
                    Admission ID
                  </Typography>

                  <Typography fontWeight={600}>
                    {detailRecord.admission ??
                      '—'}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2">
                    Date
                  </Typography>

                  <Typography fontWeight={600}>
                    {detailRecord.date ||
                      '—'}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2">
                    Status
                  </Typography>

                  <Chip
                    icon={
                      getStatusMeta(
                        detailRecord.status
                      ).icon
                    }
                    label={
                      getStatusMeta(
                        detailRecord.status
                      ).label
                    }
                    size="small"
                    color={
                      getStatusMeta(
                        detailRecord.status
                      ).color
                    }
                  />
                </Grid>

                {detailRecord.time_in && (
                  <Grid
                    item
                    xs={6}
                  >
                    <Typography variant="body2">
                      Time In
                    </Typography>

                    <Typography fontWeight={600}>
                      {
                        formatAmPmTime(
                          detailRecord.time_in
                        )
                      }
                    </Typography>
                  </Grid>
                )}

                {detailRecord.time_out && (
                  <Grid
                    item
                    xs={6}
                  >
                    <Typography variant="body2">
                      Time Out
                    </Typography>

                    <Typography fontWeight={600}>
                      {
                        formatAmPmTime(
                          detailRecord.time_out
                        )
                      }
                    </Typography>
                  </Grid>
                )}

                {detailRecord.branch !=
                  null && (
                  <Grid
                    item
                    xs={6}
                  >
                    <Typography variant="body2">
                      Branch
                    </Typography>

                    <Typography fontWeight={600}>
                      {detailRecord
                        .branch
                        ?.name ||
                        detailRecord.branch}
                    </Typography>
                  </Grid>
                )}

                {detailRecord.organization !=
                  null && (
                  <Grid
                    item
                    xs={6}
                  >
                    <Typography variant="body2">
                      Organization
                    </Typography>

                    <Typography fontWeight={600}>
                      {detailRecord
                        .organization
                        ?.name ||
                        detailRecord.organization}
                    </Typography>
                  </Grid>
                )}

                {detailRecord.marked_by && (
                  <Grid
                    item
                    xs={6}
                  >
                    <Typography variant="body2">
                      Marked By
                    </Typography>

                    <Typography fontWeight={600}>
                      {
                        detailRecord.marked_by
                      }
                    </Typography>
                  </Grid>
                )}

                {detailRecord.remark && (
                  <Grid
                    item
                    xs={12}
                  >
                    <Typography variant="body2">
                      Remark
                    </Typography>

                    <Typography fontWeight={600}>
                      {
                        detailRecord.remark
                      }
                    </Typography>
                  </Grid>
                )}
              </Grid>
            ) : (
              <Typography>
                No record data
                available.
              </Typography>
            )}
          </CustomTableScrollbar>
        </Box>
      </Modal>

      {/* =====================================================
          USER GUIDE
      ===================================================== */}

      <Modal
        open={
          showUserGuideModal
        }
        onClose={() =>
          setShowUserGuideModal(
            false
          )
        }
        BackdropProps={{
          style: {
            backgroundColor:
              'rgba(0,0,0,0.75)',
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform:
              'translate(-50%, -50%)',
            width: {
              xs: '90%',
              sm: 700,
            },
            bgcolor:
              'background.paper',
            borderRadius: '16px',
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor:
                'primary.main',
              p: '14px 20px',
              display: 'flex',
              justifyContent:
                'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
                fontWeight: 700,
              }}
            >
              Add Attendance —
              User Guide
            </Typography>

            <IconButton
              onClick={() =>
                setShowUserGuideModal(
                  false
                )
              }
              sx={{
                color: '#fff',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <CustomTableScrollbar
            sx={{
              maxHeight: '70vh',
              overflowY: 'auto',
              p: 3,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color:
                  'primary.main',
                mb: 1.5,
              }}
            >
              ➤ Filtering
              Students
            </Typography>

            <ul
              style={{
                marginLeft: 20,
                lineHeight: 2,
              }}
            >
              <li>
                Branch filters
                the available
                Course list.
              </li>

              <li>
                Course filters
                the Batch list.
              </li>

              <li>
                Search finds
                students by name
                or admission
                number.
              </li>
            </ul>

            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color:
                  'primary.main',
                mb: 1.5,
              }}
            >
              ➤ Selecting
              Students
            </Typography>

            <ul
              style={{
                marginLeft: 20,
                lineHeight: 2,
              }}
            >
              <li>
                Use the checkbox
                in the table to
                select individual
                students.
              </li>

              <li>
                Use the header
                checkbox to
                select or
                unselect all
                displayed
                students.
              </li>

              <li>
                Global Status,
                Time In, Time
                Out and Remark
                apply only to
                selected students.
              </li>
            </ul>

            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color:
                  'primary.main',
                mb: 1.5,
              }}
            >
              ➤ Marking
              Attendance
            </Typography>

            <ul
              style={{
                marginLeft: 20,
                lineHeight: 2,
              }}
            >
              <li>
                Mark All As
                changes the
                attendance
                status only for
                selected
                students.
              </li>

              <li>
                Individual
                P/A/H/L buttons
                can override the
                status.
              </li>

              <li>
                Time In is
                mandatory for
                every student
                whose attendance
                is marked.
              </li>

              <li>
                Time Out is
                optional.
              </li>

              <li>
                Time In and
                Time Out support
                AM/PM selection.
              </li>

              <li>
                The frontend
                sends time to the
                backend in
                24-hour format.
              </li>

              <li>
                Time Out must
                be greater than
                Time In.
              </li>

              <li>
                Save creates
                new records or
                updates existing
                records.
              </li>
            </ul>
          </CustomTableScrollbar>
        </Box>
      </Modal>
    </Container>
  );
};

export default AddAttendance;